import { Box, styled } from '@mui/material';
import { differenceInDays } from 'date-fns';
import { useMemo } from 'react';

import { CarIcon, currentTimeInTimezone, PlaneOnFlyIcon } from '#shared';

import {
  AvailabilityColorType,
  CasesStatisticsDataType,
  getConicGradient,
} from './colors';

import type { SimpleStatistics } from '../types';

interface Props {
  statistics: SimpleStatistics;
}

const PREFIX = 'CC-Statistics';
export const statisticsClasses = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  count: `${PREFIX}-count`,
  helperText: `${PREFIX}-helperText`,
  statusText: `${PREFIX}-statusText`,
  pieChart: `${PREFIX}-pieChart`,
  pieChartFragment: `${PREFIX}-pieChartFragment`,
  chartContent: `${PREFIX}-chartContent`,
  percents: `${PREFIX}-percents`,
  text: `${PREFIX}-text`,
  icons: `${PREFIX}-icons`,
  iconText: `${PREFIX}-iconText`,
  airGround: `${PREFIX}-airGround`,
  ground: `${PREFIX}-ground`,
  iconTextBig: `${PREFIX}-iconTextBig`,
  iconTextSmall: `${PREFIX}-iconTextSmall`,
} as const;

const Root = styled(Box, {
  name: PREFIX,
  slot: 'Root',
  overridesResolver: (props, styles) => styles.root,
})({
  [`&.${statisticsClasses.root}`]: {
    position: 'absolute',
    left: '40px',
    top: '270px',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '30px',
    fontFamily: '"JetBrains Mono", monospace',
    fontFeatureSettings: '"zero" 1',
  },

  [`& .${statisticsClasses.header}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    color: '#B6B6B6',
  },

  [`& .${statisticsClasses.count}`]: {
    fontSize: '14px',
  },

  [`& .${statisticsClasses.helperText}`]: {
    fontSize: '10px',
  },

  [`& .${statisticsClasses.statusText}`]: {
    color: '#7F8083CC',
    fontSize: '10px',
  },

  [`& .${statisticsClasses.pieChart}`]: {
    position: 'relative',
    width: '108px',
    height: '108px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  [`& .${statisticsClasses.pieChartFragment}`]: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
  },

  [`& .${statisticsClasses.chartContent}`]: {
    position: 'relative',
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    backgroundColor: '#000000',
    zIndex: 10,
    padding: '4px 11px 21px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '4px',
    boxSizing: 'border-box',
  },

  [`& .${statisticsClasses.percents}`]: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'flex-end',
    fontSize: '24px',
    fontWeight: 500,
    color: '#DDDDDD',
  },

  [`& .${statisticsClasses.text}`]: {
    fontSize: '10px',
    color: '#7F8083',
    textAlign: 'center',
  },

  [`& .${statisticsClasses.icons}`]: {
    display: 'flex',
    gap: '6px',
    alignItems: 'flex-end',
    flexGrow: 1,
  },

  [`& .${statisticsClasses.airGround}`]: {
    color: '#0E5795',
  },

  [`& .${statisticsClasses.ground}`]: {
    color: '#62666C',
  },

  [`& .${statisticsClasses.iconText}`]: {
    fontWeight: 500,
    fontSize: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    '& svg': {
      width: '22px',
      height: '22px',
      marginBottom: '-5px',
    },
  },

  [`& .${statisticsClasses.iconTextBig}`]: {
    fontSize: '24px',

    '& svg': {
      width: '30px',
      height: '30px',
      marginBottom: '-10px',
    },
  },

  [`& .${statisticsClasses.iconTextSmall}`]: {
    fontSize: '14px',
    paddingBottom: '3px',
  },
});

const AVAILABILITY_COLOR: AvailabilityColorType[] = [
  {
    main: '#006A17',
    secondary: '#00E7331A',
    minPercents: 70,
  },
  {
    main: '#8B5C00',
    secondary: '#FFAF3833',
    minPercents: 50,
  },
  {
    main: '#851D24',
    secondary: '#FF384433',
    minPercents: 0,
  },
];

const GROUND_COLOR = '#62666C';
const AIR_GROUND_COLOR = '#0E5795';

export const Statistics = ({ statistics }: Props) => {
  const casesStatisticsData = useMemo<CasesStatisticsDataType>(() => {
    const { allCases, airGround, tmdxShare } = statistics.casesStatistics;

    const airGroundShare = Math.round((airGround / allCases) * 100);
    const groundShare = 100 - airGroundShare;
    const tmdxSharePercents = Math.round((tmdxShare / airGround) * 100);
    const tmdxShareColors =
      AVAILABILITY_COLOR.find(i => i.minPercents <= tmdxSharePercents) ||
      AVAILABILITY_COLOR[0];

    return {
      airGround: airGroundShare,
      ground: groundShare,
      tmdxShare: tmdxSharePercents,
      airGroundBg: getConicGradient({
        mainColor: GROUND_COLOR,
        secondaryColor: AIR_GROUND_COLOR,
        mainPercents: groundShare,
      }),
      tmdxShareBg: getConicGradient({
        mainColor: tmdxShareColors.main,
        secondaryColor: tmdxShareColors.secondary,
        mainPercents: tmdxSharePercents,
      }),
    };
  }, [statistics.casesStatistics]);

  const daysCount = useMemo<number>(() => {
    const dateNow = new Date();
    const startDate = new Date(statistics.casesStatistics.startDate);

    return differenceInDays(dateNow, startDate) || 1;
  }, [statistics.casesStatistics.startDate]);

  return (
    <Root className={statisticsClasses.root}>
      <div className={statisticsClasses.header}>
        <span className={statisticsClasses.count}>
          {statistics.casesStatistics.allCases} Cases
        </span>
        <span className={statisticsClasses.helperText}>
          for last {daysCount} days
        </span>
      </div>
      <div className={statisticsClasses.pieChart}>
        <div
          className={statisticsClasses.pieChartFragment}
          style={{
            background: casesStatisticsData.airGroundBg,
          }}
        />
        <div className={statisticsClasses.chartContent}>
          <div className={statisticsClasses.icons}>
            <div
              className={`${statisticsClasses.iconText} ${statisticsClasses.airGround}${
                casesStatisticsData.airGround > 50
                  ? ` ${statisticsClasses.iconTextBig}`
                  : casesStatisticsData.airGround < 50
                    ? ` ${statisticsClasses.iconTextSmall}`
                    : ''
              }`}
            >
              <PlaneOnFlyIcon />
              <span>{casesStatisticsData.airGround}%</span>
            </div>
            <div
              className={`${statisticsClasses.iconText} ${statisticsClasses.ground}${
                casesStatisticsData.ground > 50
                  ? ` ${statisticsClasses.iconTextBig}`
                  : casesStatisticsData.ground < 50
                    ? ` ${statisticsClasses.iconTextSmall}`
                    : ''
              }`}
            >
              <CarIcon />
              <span>{casesStatisticsData.ground}%</span>
            </div>
          </div>
          <span
            className={statisticsClasses.text}
            style={{
              letterSpacing: '-0.09em',
            }}
          >
            Air vs Ground
          </span>
        </div>
      </div>
      <span className={statisticsClasses.statusText}>
        Last update{' '}
        {currentTimeInTimezone(
          new Date(statistics.lastUpdated),
          'America/New_York',
        )}{' '}
        ET
      </span>
    </Root>
  );
};
